import React, { ReactNode, useEffect } from "react";
import styled from "styles/styled_components";

import Checkbox from "components/shared/checkbox_with_label";
import Dropdown, { IOption, DROPDOWN_SIZE } from "components/shared/dropdown";
import Warning from "components/shared/warning";

import useDropdownOption from "helpers/hooks/useDropdownOption";
import useToggle from "helpers/hooks/useToggle";
import { stringToMultiline } from "helpers/general";
import {
  BLANK_TIME,
  EMPLOYEE_DEFAULT,
  employeeSelected,
  getEmployeeOptionsByRole,
  getEmployeeRoles,
  ROLE_DEFAULT,
  roleSelected,
  SHIFT_HOURS,
  SHIFT_MINUTES,
  IEmployeeDropdownOption,
  IFrequencyDropdownOption,
  IRoleDropdownOption,
  ITimeDropdownOption,
  getFrequencyOptions,
} from "helpers/schedule/time_block_modal";
import { ISchedule } from "types/schedules";
import useFormOnSubmit from "helpers/hooks/useFormOnSubmit";
import LabeledDropdown from "components/shared/labeled_dropdown";

const StyledForm = styled.form`
  text-align: left;
`;

const StyledFormSection = styled.div`
  margin: 0 0 ${({ theme }) => theme.spacing.large};
  width: 100%;
`;

// @ts-ignore TODO please fix this error
const StyledTimeCombo = styled(StyledFormSection)`
  display: inline-flex;
  flex-wrap: wrap;
`;

const StyledCheckboxWrapper = styled.div`
  margin-left: ${({ theme }) => 10 * theme.rawGridBase - 5}px;
  margin-top: ${({ theme }) => theme.spacing.small};
`;

const StyledWarning = styled(Warning)`
  padding: 0;
  margin: 0 auto ${({ theme }) => theme.spacing.medium};
  max-width: 90%;
`;

export interface IFormValues {
  allDay?: boolean;
  employee: IEmployeeDropdownOption;
  endHour: ITimeDropdownOption;
  endMinute: ITimeDropdownOption;
  frequency: IFrequencyDropdownOption;
  role: IRoleDropdownOption;
  date: IOption<Date>;
  startHour: ITimeDropdownOption;
  startMinute: ITimeDropdownOption;
}

export interface ITimeBlockFormErrors {
  conflict: string;
  employee: string;
  role: string;
}

export const DEFAULT_ERRORS: ITimeBlockFormErrors = {
  conflict: "",
  employee: "",
  role: "",
};

export interface ITimeBlockForm {
  allEmployeeOptions: IEmployeeDropdownOption[];
  allRoleOptions: IRoleDropdownOption[];
  errors: ITimeBlockFormErrors;
  initValues: IFormValues;
  isUnavail: boolean;
  isCreating: boolean;
  schedule: ISchedule;
  scheduleDates: Array<IOption<Date>>;
  onSave: () => void;
  onChange: (formValues: IFormValues) => void;
  children?: ReactNode;
}

export default function TimeBlockForm({
  allEmployeeOptions,
  allRoleOptions,
  children,
  errors,
  initValues,
  isCreating,
  isUnavail,
  schedule,
  scheduleDates,
  onChange,
  onSave,
}: ITimeBlockForm) {
  // Dropdown Options - build handlers with useDropdownOption hook,
  // for all the values in the form. Initialize with props.initValues
  const [employee, handleEmployeeClick] = useDropdownOption(
    initValues.employee
  );
  const [role, handleRoleClick] = useDropdownOption(initValues.role);
  const [date, handleDateClick] = useDropdownOption(initValues.date);
  const [frequency, handleFrequencyClick] = useDropdownOption(
    initValues.frequency
  );
  const [startHour, handleStartHourClick] = useDropdownOption(
    initValues.startHour
  );
  const [startMinute, handleStartMinuteClick] = useDropdownOption(
    initValues.startMinute
  );
  const [endHour, handleEndHourClick] = useDropdownOption(initValues.endHour);
  const [endMinute, handleEndMinuteClick] = useDropdownOption(
    initValues.endMinute
  );
  const [allDay, toggleAllDay] = useToggle(initValues.allDay || false);

  // Pass values up on change
  useEffect(() => {
    onChange({
      allDay,
      date,
      employee,
      endHour,
      endMinute,
      frequency,
      role,
      startHour,
      startMinute,
    });
  }, [
    allDay,
    date,
    employee,
    endHour,
    endMinute,
    frequency,
    role,
    startHour,
    startMinute,
  ]);

  // Get scoped roles and scoped employees based on selected employee/role
  let scopedRoles: IRoleDropdownOption[] = allRoleOptions;
  if (employeeSelected(employee)) {
    scopedRoles = getEmployeeRoles(
      employee.meta.andThen((employeePerson) => employeePerson.assignee)
    );
  }

  let scopedEmployees: IEmployeeDropdownOption[] = allEmployeeOptions;
  if (roleSelected(role)) {
    scopedEmployees = role.meta
      .map((selectedRole) =>
        getEmployeeOptionsByRole(selectedRole, schedule.people)
      )
      .unwrapOr(allEmployeeOptions);
  }

  // Conditionally rendered components
  const roleDropdown = !isUnavail && (
    <StyledFormSection>
      <LabeledDropdown
        label="Role"
        currentOption={role}
        dropdownName="role"
        errorMessage={errors.role}
        onOptionClick={handleRoleClick}
        options={[ROLE_DEFAULT, ...scopedRoles]}
        size={DROPDOWN_SIZE.FULL}
      />
    </StyledFormSection>
  );
  const frequencyDropdown = isUnavail && (
    <StyledFormSection>
      <LabeledDropdown
        label="Frequency"
        currentOption={frequency}
        dropdownName="frequency"
        onOptionClick={handleFrequencyClick}
        options={getFrequencyOptions(date.meta)}
        size={DROPDOWN_SIZE.FULL}
      />
    </StyledFormSection>
  );

  const allDayCheckbox = isUnavail && (
    <StyledCheckboxWrapper>
      <Checkbox
        label="Unavailable all day"
        checked={allDay}
        onChangeHandler={toggleAllDay}
      />
    </StyledCheckboxWrapper>
  );

  const employeeDropdown = (
    <LabeledDropdown
      label="Employee"
      currentOption={employee}
      disabled={isUnavail && !isCreating}
      dropdownName="employee"
      errorMessage={errors.employee}
      onOptionClick={handleEmployeeClick}
      options={[EMPLOYEE_DEFAULT, ...scopedEmployees]}
      size={DROPDOWN_SIZE.FULL}
    />
  );

  return (
    <StyledForm onSubmit={useFormOnSubmit(onSave)}>
      <StyledFormSection>{employeeDropdown}</StyledFormSection>

      {roleDropdown}

      <StyledFormSection>
        <LabeledDropdown
          label="Date"
          currentOption={date}
          dropdownName="date"
          onOptionClick={handleDateClick}
          options={scheduleDates}
          size={DROPDOWN_SIZE.FULL}
        />
      </StyledFormSection>

      {frequencyDropdown}

      <StyledTimeCombo>
        <LabeledDropdown
          label="Start"
          currentOption={!allDay || isCreating ? startHour : BLANK_TIME}
          disabled={isUnavail && allDay}
          dropdownName="startHour"
          onOptionClick={handleStartHourClick}
          options={SHIFT_HOURS}
          size={DROPDOWN_SIZE.SMALL}
        />
        <Dropdown
          currentOption={!allDay || isCreating ? startMinute : BLANK_TIME}
          disabled={isUnavail && allDay}
          dropdownName="startMinutes"
          onOptionClick={handleStartMinuteClick}
          options={SHIFT_MINUTES}
          size={DROPDOWN_SIZE.SMALL}
        />
      </StyledTimeCombo>
      <StyledTimeCombo>
        <LabeledDropdown
          label="End"
          currentOption={!allDay || isCreating ? endHour : BLANK_TIME}
          disabled={isUnavail && allDay}
          dropdownName="endHours"
          onOptionClick={handleEndHourClick}
          options={SHIFT_HOURS}
          size={DROPDOWN_SIZE.SMALL}
        />
        <Dropdown
          currentOption={!allDay || isCreating ? endMinute : BLANK_TIME}
          disabled={isUnavail && allDay}
          dropdownName="endMinutes"
          onOptionClick={handleEndMinuteClick}
          options={SHIFT_MINUTES}
          size={DROPDOWN_SIZE.SMALL}
        />
        {allDayCheckbox}
      </StyledTimeCombo>
      {errors.conflict && (
        <StyledWarning align="center">
          {stringToMultiline(errors.conflict)}
        </StyledWarning>
      )}
      {children}
    </StyledForm>
  );
}
